import { api, type DokumentumAdat } from '../api.ts';
import {
  ALAP_DOKUMENTUM_CSS,
  ablakotBezar,
  ablakotNyit,
  dokumentumMegjelenites,
  esc,
  huDatum,
} from './dokumentum-ablak.ts';

/**
 * A három szerződéses dokumentum szövege. (Megbízás 2.0 / 5.7)
 *
 * A dokumentumok **külön ablakban nyílnak, saját stíluslappal** — nem a fő
 * felület nyomtatási CSS-ére épülnek. Így a szerződés akkor is ugyanúgy néz
 * ki, ha a program felülete később változik.
 *
 * Minden adat a szerverről jön: a cégadatok a beállításokból, a hordó mérete
 * és a betétdíj a termékek törzséből. Semmi nincs ide égetve.
 */

/* --- apró segédek --------------------------------------------------- */

function ft(n: number): string {
  return new Intl.NumberFormat('hu-HU').format(Math.round(n));
}

/** Kitöltött vonal a szerződésben — a nyomtatott űrlap kinézete miatt. */
function vonal(szoveg: unknown, szelesseg = 120): string {
  return `<span class="kt" style="min-width:${szelesseg}px">${esc(szoveg)}</span>`;
}

const SZAMSZO = [
  'nulla','egy','kettő','három','négy','öt','hat','hét','nyolc','kilenc','tíz',
  'tizenegy','tizenkettő','tizenhárom','tizennégy','tizenöt','tizenhat','tizenhét',
  'tizennyolc','tizenkilenc',
];
const TIZ = ['','tíz','húsz','harminc','negyven','ötven','hatvan','hetven','nyolcvan','kilencven'];

/**
 * Szám betűvel. A HSO szerződésben az átvételi árat számmal **és betűvel**
 * is ki kell írni — ez jogi követelmény, nem díszítés.
 */
export function szoval(n: number): string {
  const sz = Math.round(n);
  if (sz < 0) return `mínusz ${szoval(-sz)}`;
  if (sz < 20) return SZAMSZO[sz]!;
  if (sz < 100) {
    const t = Math.floor(sz / 10);
    const e = sz % 10;
    const elo = t === 2 ? 'huszon' : t === 3 ? 'harminc' : TIZ[t]!;
    return e ? elo + SZAMSZO[e] : TIZ[t]!;
  }
  if (sz < 1000) {
    const s = Math.floor(sz / 100);
    const m = sz % 100;
    return (s === 1 ? 'száz' : `${s === 2 ? 'két' : SZAMSZO[s]}száz`) + (m ? szoval(m) : '');
  }
  if (sz < 1_000_000) {
    const e = Math.floor(sz / 1000);
    const m = sz % 1000;
    return (e === 1 ? 'ezer' : `${e === 2 ? 'két' : szoval(e)}ezer`) + (m ? szoval(m) : '');
  }
  return String(sz);
}

/** A telephely teljes címe. Budapestnél a helységet nem ismételjük. */
function telephelyCim(p: DokumentumAdat['partner']): string {
  const helyseg =
    p.helyseg && !/bp|budapest/i.test(p.cim ?? '') ? ` ${p.helyseg}` : '';
  return `${p.irsz ? `${p.irsz} ` : ''}${p.cim ?? ''}${helyseg}`.trim();
}

/* --- MO szerződés --------------------------------------------------- */

export function moSzerzodes(a: DokumentumAdat): string {
  const { ceg: c, partner: p, termek: t, beallitas: b } = a;

  return `<div class="lap szerz">
  <div class="szfej">
    <div class="szbal">KSH: ${esc(c.ksh)}<br>Eng. szám: ${esc(c.engedely)}<br>Adószám: ${esc(c.adoszam)}<br><b>${esc(b.szerzodesszam)}</b></div>
    <div class="pecset"></div>
  </div>
  <h2 class="szcim">SZERZŐDÉS</h2>
  <p class="szp">Alulírott felek a biológiailag bomló konyhai és étkezési hulladékok,
    3. kategóriájú állati melléktermékek átvétele és elszállítása tárgyában az alábbi
    feltételekben egyeznek meg.</p>

  <p class="szp"><b>1. Az ${esc(c.teljes_nev)} vállalja (Szállító és egyben Átvevő), hogy</b></p>
  <p class="szp li">– betétdíj ellenében felállít ${vonal(b.darab, 40)} db ${vonal(t.hordo_liter, 50)}
    literes műanyag hordót a ${vonal(p.nev, 220)} nevű, ${vonal(p.adoszam ?? '—', 150)} adószámú,
    ${vonal(telephelyCim(p), 260)} (cím) gazdálkodó szervezetnél (a továbbiakban Átadó),</p>
  <p class="szp li">– az Átadó által összegyűjtött konyhai és étkezési hulladékot megegyezés
    szerint körjáratszerűen vagy telefonhívásra legkésőbb 72 órán belül elszállítja az Átadótól,</p>
  <p class="szp li">– az átadás illetve átvétel tényét a mindenkor aktuális törvényben
    előírtaknak megfelelően dokumentálja.</p>

  <p class="szp"><b>2. Az Átadó vállalja, hogy</b></p>
  <p class="szp li">– a biológiailag bomló konyhai és étkezési hulladékot, azaz 3. kategóriájú
    állati mellékterméket egyéb hulladékoktól mentesen az Átvevő által biztosított edényzetben
    összegyűjti,</p>
  <p class="szp li">– abban az esetben, ha a hordók megteltek, az Átvevőt a ${esc(c.telefon)}
    telefonszámok valamelyikén értesíti,</p>
  <p class="szp li">– az Átadónál felállított hordók megőrzéséről gondoskodik, azok
    megrongálódása illetve elvesztése esetén anyagi felelősséggel tartozik.</p>

  <p class="szp"><b>3.</b> A hordó betétdíjának összege nettó ${ft(t.hordo_betetdij)} Ft (darabonként),
    melyet az Átadó a hordó kihelyezésekor ad át Szállítónak szállítólevél ellenében. A Szállító
    a szerződés felbontása után 20 napos határidővel, de legkésőbb a szerződés érvényességének
    végéig (3 hónap) visszaadja a betétdíjat Átadónak, amennyiben az általa kihelyezett hordó
    sértetlen, eredeti állapotban visszaadásra kerül.</p>

  <p class="szp"><b>4.</b> Ezen szerződést a felek meghatározott időtartamra kötik, a szerződés
    létrejöttét követően, ${vonal(huDatum(b.lejarat), 190)} után érvényét veszti. Ezen időpontig a
    szerződést mindkét szerződött félnek joga van indoklás nélkül írásban 20 napos határidővel
    felmondani.</p>

  <p class="szp"><b>5.</b> Jelen szerződésben nem szabályozott kérdésekben a Ptk. rendelkezései
    irányadóak.</p>

  <p class="szp szkelt">Kelt: ${vonal(b.hely, 120)}, ${huDatum(b.kelt)}</p>
  <div class="szalair">
    <div><span class="al"></span>Átadó</div>
    <div><span class="al"></span>Átvevő</div>
  </div>
</div>`;
}

/* --- HSO szerződés -------------------------------------------------- */

export function hsoSzerzodes(a: DokumentumAdat): string {
  const { ceg: c, partner: p, termek: t, beallitas: b } = a;
  const ar = t.hso_ar;

  const sor = (cimke: string, ertek: unknown, szel = 300) =>
    `<tr><td class="l">${cimke}</td><td>${vonal(ertek, szel)}</td></tr>`;

  return `<div class="lap szerz">
  <div class="szfej">
    <div class="szbal"><b>${esc(c.teljes_nev)}</b><br>${esc(c.szekhely)} Telefon: ${esc(c.telefon)}<br>
      Fax: ${esc(c.fax)}<br><br>KSH: ${esc(c.ksh)}<br>Eng. szám: ${esc(c.engedely)}<br><b>${esc(b.szerzodesszam)}</b></div>
    <div class="pecset"></div>
  </div>
  <h2 class="szcim">SZERZŐDÉS</h2>
  <p class="szalcim">használt sütőolaj átvételéről, begyűjtéséről</p>

  <p class="szp">Alulírott felek a használt sütőolaj, mint 3. kategóriájú állati melléktermék
    (a továbbiakban: HSO) átvétele, begyűjtése tárgyában az alábbiakban állapodnak meg.</p>
  <p class="szp"><b>Átvevő (egyben szállító):</b> ${esc(c.rovid_nev)} ${esc(c.szekhely)} ${esc(c.adoszam)}.</p>

  <table class="szt">
    ${sor('Átadó — Cégnév:', p.cegnev ?? '')}
    ${sor('Adószám:', p.adoszam ?? '')}
    ${sor('Székhely:', p.szekhely ?? p.ceg_szekhely ?? '')}
    ${sor('Telephely (keletkezés helye):', `${p.nev} — ${telephelyCim(p)}`)}
  </table>

  <p class="szp"><b>1.</b> Az Átvevő vállalja, hogy az Átadó telephelyén keletkező használt
    sütőolajat (HSO) rendszeresen átveszi és elszállítja, az Átadó pedig vállalja, hogy az általa
    gyűjtött HSO-t a jelen szerződés szerinti minőségben az Átvevő részére átadja. Az Átvevő az
    átvett HSO-ért a 3. pontban meghatározott átvételi ár szerinti ellenértéket fizet az Átadó
    részére. Amennyiben jelen HSO állati eredetű élelmiszerrel – így különösen hús-, hal-, tojás-
    vagy tejtermékkel – érintkezett, úgy az az 1069/2009/EK európai parlamenti és tanácsi rendelet
    10. cikk p) pontja alapján 3. kategóriájú állati eredetű mellékterméknek minősül, és a Felek azt
    ennek a besorolásnak megfelelően kezelik. A HSO besorolása – mint a gazdálkodó szervezetet terhelő
    kötelezettség – az Átadó felelőssége, mivel kizárólag az Átadó rendelkezik az ehhez szükséges
    információval; a besorolást az Átadó a jelen szerződés mellékletét képező besorolási
    nyilatkozatban rögzíti.</p>

  <p class="szp"><b>2.</b> Az Átvevő kizárólag tiszta, szűrt használt sütőolajat vesz át. Az átvételre
    kerülő HSO-nak víztől, ételmaradéktól, üledéktől, valamint minden egyéb idegen anyagtól és
    szennyeződéstől mentesnek kell lennie, és nem keveredhet olyan olajjal vagy zsírral, amely a
    használt sütőolaj fogalmának nem felel meg. Az Átadó szavatolja, hogy az átadott HSO a fenti
    követelményeknek megfelel, továbbá hogy azt nem módosította és nem szennyezte szándékosan. Az
    Átvevő jogosult a HSO minőségét az átvétel helyszínén ellenőrizni, és a feltételeknek meg nem
    felelő tételt – részben vagy egészben – visszautasítani.</p>

  <p class="szp"><b>3.</b> Az átvételi ár ${vonal(ar, 60)} Ft/kg, azaz ${vonal(szoval(ar), 210)}
    forint kilogrammonként (nettó összeg), amely kizárólag a tiszta, szűrt és ténylegesen átvett
    mennyiségre vonatkozik. A visszautasított, illetve a 2. pont szerinti minőségi feltételeknek meg
    nem felelő mennyiség után az Átvevőt fizetési kötelezettség nem terheli.</p>

  <p class="szp"><b>4.</b> Az Átvevő az Átadó részére ${vonal(b.darab, 40)} db gyűjtőedényt biztosít,
    darabonként nettó ${vonal(ft(t.hordo_betetdij), 80)} Ft betétdíj ellenében. Az Átadó a gyűjtőedényt
    rendeltetésszerűen használja és megőrzi, annak megrongálódásáért vagy elvesztéséért anyagi
    felelősséggel tartozik. A betétdíjat az Átvevő a szerződés megszűnését követően, a gyűjtőedény
    sértetlen, eredeti állapotban történő visszaszolgáltatása esetén az Átadó részére visszafizeti.
    Az összegyűjtött HSO-t az Átvevő megállapodás szerint körjárat keretében, illetve az Átadó
    telefonos jelzését követően – legkésőbb 72 órán belül – szállítja el.</p>

  <p class="szp"><b>5.</b> A 3. kategóriájú HSO elszállítását a 45/2012. (V. 8.) VM rendeletben
    szereplő kereskedelmi okmány kíséri a jogszabályban meghatározott tartalommal és aláírásokkal.
    A Felek a szállítási és átvételi dokumentumokat a jogszabályi előírásnak megfelelően legalább
    két évig megőrzik.</p>

  <p class="szp"><b>6.</b> A jelen szerződés a Felek aláírásának napján lép hatályba, és
    határozatlan időtartamra jön létre. A szerződést bármelyik fél jogosult indokolás nélkül,
    írásban, 30 (harminc) napos felmondási idővel felmondani. Súlyos szerződésszegés esetén – így
    különösen szennyezett, kevert vagy szándékosan módosított HSO átadása esetén – a sérelmet
    szenvedő fél a szerződést írásban, azonnali hatállyal felmondhatja.</p>

  <p class="szp"><b>7.</b> A jelen szerződésben nem szabályozott kérdésekben a Ptk. az irányadó.</p>

  <p class="szp szkelt">Kelt: ${vonal(b.hely, 150)}, ${huDatum(b.kelt)}</p>
  <div class="szalair">
    <div><span class="al"></span>Átadó</div>
    <div><span class="al"></span>Átvevő (${esc(c.rovid_nev)})</div>
  </div>
</div>`;
}

/* --- Nyilatkozat ---------------------------------------------------- */

export function nyilatkozat(a: DokumentumAdat): string {
  const { ceg: c, partner: p, beallitas: b } = a;
  const sor = (cimke: string, ertek: unknown) =>
    `<tr><td class="l">${cimke}</td><td>${esc(ertek)}</td></tr>`;

  return `<div class="lap szerz nyil">
  <h2 class="szcim">NYILATKOZAT</h2>
  <p class="szalcim">a használt sütőolaj (HSO) eredetéről és besorolásáról</p>

  <p class="szp"><b>1. A nyilatkozó vendéglátóegység adatai (Átadó)</b></p>
  <table class="nyt">
    ${sor('Étterem / egység neve:', p.nev)}
    ${sor('Cég / üzemeltető neve:', p.cegnev ?? '')}
    ${sor('Székhely:', p.szekhely ?? p.ceg_szekhely ?? '')}
    ${sor('Telephely (keletkezés helye):', telephelyCim(p))}
    ${sor('Adószám:', p.adoszam ?? '')}
    ${sor('Cégjegyzék / nyilvántartási szám:', '')}
    ${sor('Kapcsolattartó, telefonszám:', p.telefon ?? '')}
  </table>
  <p class="szp mini">(a továbbiakban: Átadó)</p>

  <p class="szp"><b>2. Nyilatkozat a sütőolaj besorolásáról</b></p>
  <p class="szp">Az Átadó kijelenti, hogy a telephelyén keletkező használt sütőolaj besorolása az
    alábbiak szerint történik. Az Átadó tudomásul veszi, hogy a sütőolaj minősítése a gazdálkodó
    szervezet (Átadó) kötelezettsége és felelőssége, mivel kizárólag ő rendelkezik az arra vonatkozó
    információval, hogy a sütőolaj érintkezett-e állati eredetű anyaggal.</p>
  <p class="szp"><i>Kérjük, az alábbi két variáns közül a sütőolajára vonatkozót jelölje X-szel.
    A jelölés kötelező, és a kettő közül kizárólag egy választható; jelölés hiányában, illetve
    mindkét opció megjelölése esetén a nyilatkozat érvénytelen.</i></p>

  <p class="szp var"><span class="nbox"></span><b>A) variáns – Kizárólag növényi eredetű HSO</b><br>
    A használt sütőolaj kizárólag növényi eredetű alapanyaggal érintkezett; állati eredetű
    élelmiszerrel (hús, hal, tojás, tejtermék) nem érintkezett, ilyennel nem keveredett. Így a HSO a
    hulladékgazdálkodási (koncessziós) rendszer hatálya alá tartozik, elszállítása a MOHU MOL
    Hulladékgazdálkodási Zrt. (illetve közreműködője) útján történik.</p>

  <p class="szp var"><span class="nbox"></span><b>B) variáns – Állati eredetű anyaggal érintkezett
    HSO (3. kategóriájú állati melléktermék)</b><br>
    A használt sütőolaj állati eredetű élelmiszerrel (hús, hal, tojás, tejtermék vagy egyéb állati
    eredetű alapanyag) érintkezett, illetve ilyennel keveredett. Így a HSO az 1069/2009/EK rendelet
    10. cikk p) pontja alapján 3. kategóriájú állati eredetű melléktermék, amely nem tartozik a
    hulladékkoncesszió átadási kötelezettsége alá. Az Átadó a HSO-t e besorolásnak megfelelően,
    3. kategóriájú állati melléktermékként adja át a Begyűjtő részére.</p>

  <p class="szp mini">(Telephelyenként eltérő besorolás esetén telephelyenként külön nyilatkozat
    töltendő ki.)</p>

  <p class="szp"><b>3. A begyűjtő (átvevő) adatai (Begyűjtő)</b></p>
  <table class="nyt">
    ${sor('Begyűjtő cég neve:', c.rovid_nev)}
    ${sor('Székhely:', c.szekhely)}
    ${sor('Adószám:', c.adoszam)}
    ${sor('ÁMT-engedély / nyilvántartási szám:', c.engedely)}
  </table>
  <p class="szp mini">(a továbbiakban: Begyűjtő)</p>

  <p class="szp"><b>4. Jogi háttér és érvényesség</b></p>
  <p class="szp">A nyilatkozat alapját képező főbb jogszabályok: 1069/2009/EK, 142/2011/EU és
    (EU) 2025/2181 rendelet; a 45/2012. (V. 8.) VM rendelet (a belföldi szállítást kísérő kereskedelmi
    okmány); valamint a 272/2023. (VI. 29.) Korm. rendelet.</p>
  <p class="szp">A „B) variáns" szerinti besorolás esetén az elszállítást a 45/2012. (V. 8.) VM
    rendelet szerinti kereskedelmi okmány kíséri, és a begyűjtés kizárólag állati melléktermék
    kezelésére érvényes engedéllyel rendelkező Begyűjtő útján történhet. Az Átadó vállalja, hogy a
    besorolás megváltozása esetén (pl. receptúra-, technológiaváltás) haladéktalanul, írásban
    értesíti a Begyűjtőt. A nyilatkozat a keltezéstől visszavonásig, illetve a besorolás
    megváltozásáig érvényes.</p>
  <p class="szp">Alulírott kijelentem, hogy a fenti adatok a valóságnak megfelelnek, és a
    nyilatkozat megtételére jogosultsággal rendelkezem.</p>

  <p class="szp szkelt">Kelt: ${vonal(`${b.hely}, ${huDatum(b.kelt)}`, 300)} (hely, dátum)</p>
  <div class="szalair">
    <div><span class="al"></span>Átadó cégszerű aláírása</div>
    <div><span class="al"></span>Begyűjtő átvevő aláírása</div>
  </div>
  <p class="szp mini" style="margin-top:18px">A formanyomtatványt biztosítja: ${esc(c.rovid_nev)}
    &nbsp;·&nbsp; ${esc(c.szekhely)} &nbsp;·&nbsp; E-mail/Tel.: ${esc(c.email)}, ${esc(c.telefon)}</p>
</div>`;
}

/* --- nyomtatás ----------------------------------------------------- */

/**
 * Egy szerződés nyomtatása. MO-nál a moslékszerződés; FO-nál a HSO
 * szerződés **és a nyilatkozat** — a szerződés szövege szerint a
 * nyilatkozat a melléklete.
 *
 * Az ablakot a kattintásban kell megnyitni (ezért alapértelmezett
 * paraméter): a hálózati válasz után a böngésző már letiltaná. Ha így sem
 * nyílt meg, a dokumentum a felület fölötti rétegben jelenik meg.
 */
export async function szerzodesNyomtatas(
  id: number,
  ablak: Window | null = ablakotNyit(SZERZODES_CSS),
): Promise<void> {
  try {
    const adat = await api.get<DokumentumAdat>(`/api/szerzodes/${id}/dokumentum`);
    const mo = adat.szerzodes.tipus === 'MO';
    const lapok = mo ? moSzerzodes(adat) : hsoSzerzodes(adat) + nyilatkozat(adat);
    const cim = `${mo ? 'MO szerződés' : 'HSO szerződés és nyilatkozat'} — ${adat.partner.nev}`;
    dokumentumMegjelenites(ablak, cim, lapok, SZERZODES_CSS);
  } catch (hiba) {
    ablakotBezar(ablak);
    throw hiba;
  }
}

/**
 * A szerződés önálló HTML-lapként, a partner adataival együtt. Ezt küldjük
 * e-mailben — ugyanaz a szöveg és forma, mint nyomtatásban.
 */
export async function szerzodesLap(
  id: number,
): Promise<{ html: string; cim: string; partner: string; etterem_id: number | null }> {
  const adat = await api.get<DokumentumAdat>(`/api/szerzodes/${id}/dokumentum`);
  const mo = adat.szerzodes.tipus === 'MO';
  const lapok = mo ? moSzerzodes(adat) : hsoSzerzodes(adat) + nyilatkozat(adat);
  const cim = `${mo ? 'MO szerződés' : 'HSO szerződés és nyilatkozat'} — ${adat.partner.nev}`;
  return {
    cim,
    partner: adat.partner.nev,
    etterem_id: adat.szerzodes.etterem_id ?? null,
    html:
      '<!doctype html><html lang="hu"><head><meta charset="utf-8">' +
      `<title>${cim}</title><style>${ALAP_DOKUMENTUM_CSS}${SZERZODES_CSS}</style></head>` +
      `<body><div class="lapok">${lapok}</div></body></html>`,
  };
}

/**
 * A szerződés PDF-ben, új lapon. (2026-10-06)
 *
 * Böngészőből nyomtatva az egyik irodai nyomtató „PCLm ERROR" lapot adott
 * a nyilatkozat helyett. A PDF-et a szerver készíti, ugyanazzal a motorral,
 * amivel a szállítóleveleket — azok hibátlanul nyomtatódnak. A PDF-olvasóból
 * nyomtatva a nyomtató kész oldalakat kap, nem neki kell kirajzolnia.
 */
export async function szerzodesPdf(id: number): Promise<void> {
  const lap = await szerzodesLap(id);
  const valasz = await fetch(`/api/szerzodes/${id}/pdf`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ html: lap.html }),
  });
  if (!valasz.ok) {
    const hiba = (await valasz.json().catch(() => null)) as { hiba?: string } | null;
    throw new Error(hiba?.hiba ?? 'A PDF nem készült el.');
  }
  const url = URL.createObjectURL(await valasz.blob());
  window.open(url, '_blank', 'noopener');
  /* A böngésző a megnyitás után is használja; egy perc bőven elég neki. */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* --- a külön ablak -------------------------------------------------- */

/**
 * A dokumentumok saját stíluslapja.
 *
 * Szándékosan **nem** a fő felület CSS-ére épül: egy szerződés akkor is
 * ugyanúgy nézzen ki, ha a program felülete később megváltozik.
 */
export const SZERZODES_CSS = `
  .szfej { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
  .szbal { font-size: 9.5pt; line-height: 1.35; }
  .pecset { width: 42mm; height: 24mm; border: 1px dashed #999; }
  .szcim { text-align: center; font-size: 16pt; letter-spacing: .12em; margin: 10px 0 2px; }
  .szalcim { text-align: center; font-size: 11pt; font-style: italic; margin: 0 0 14px; }
  .szp { margin: 0 0 8px; text-align: justify; }
  .szp.li { margin-left: 14px; }
  .szp.mini { font-size: 9pt; color: #333; }
  .szp.var { border: 1px solid #000; padding: 8px 10px; margin-bottom: 10px; }
  .nbox { display: inline-block; width: 13px; height: 13px; border: 1.5px solid #000;
          margin-right: 8px; vertical-align: -2px; }
  .kt { display: inline-block; border-bottom: 1px solid #000; padding: 0 4px; font-weight: 600; }
  .szt, .nyt { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
  .nyt td { border: 1px solid #000; padding: 5px 7px; font-size: 10pt; }
  .szt td { padding: 3px 4px; }
  .szt .l, .nyt .l { width: 42%; font-weight: 600; }
  .szkelt { margin-top: 18px; }
  .szalair { display: flex; gap: 40px; margin-top: 26px; text-align: center; font-size: 10pt; }
  .szalair > div { flex: 1; }
  .al { display: block; border-top: 1px solid #000; margin-bottom: 4px; height: 34px; }
`;
